import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { getErrorCode, getRequestUrl, getType, prefixApiUrl } from "helpers";
import { createLogger } from "logging";
import { useAuthentication, useTranslations } from "providers";
import React, { PropsWithChildren, useMemo } from "react";
import { AgendaItemsApi } from "services/AgendaItemsApi";
import { BoardApi } from "services/BoardApi";
import { BoardMembersApi } from "services/BoardMembersApi";
import { BoardsApi } from "services/BoardsApi";
import { BoardSendToApi } from "services/BoardSendToApi";
import { DashboardApi } from "services/DashboardApi";
import { DownloadApi } from "services/DownloadApi";
import { EdecideApi } from "services/EdecideApi";
import { MeetingApi } from "services/MeetingApi";
import { MeetingsApi } from "services/MeetingsApi";
import { OneDriveApi } from "services/OneDriveApi";
import { OutputDocumentsApi } from "services/OutputDocumentsApi";
import { ParticipantsApi } from "services/ParticipantsApi";
import { PermissionsApi } from "services/PermissionsApi";
import { SendToApi } from "services/SendToApi";
import { StandardMeetingApi as StandardMeetingsApi } from "services/StandardMeetingsApi";
import { SuggestedAgendaItemsApi } from "services/SuggestedAgendaItemsApi";
import { TasksApi } from "services/TasksApi";
import { TemplatesApi } from "services/TemplatesApi";
import { UserApi } from "services/UserApi";
import { WorkingDocumentsApi } from "services/WorkingDocumentsApi";

import { ApiContext } from "./ApiContext";

const logger = createLogger("ApiProvider");

export const ApiProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const { acquireTokenSilent } = useAuthentication();

  const acceptLanguage = `${i18n.currentLanguage || "de"}-CH`;

  const axiosInst = useMemo(() => {
    const axiosInst = axios.create();

    // Request interceptor: Add auth token to all requests
    const interceptors = axiosInst.interceptors;
    interceptors.request.use(
      async (request: InternalAxiosRequestConfig) => {
        const accessToken = await acquireTokenSilent();
        request.headers.authorization = `Bearer ${accessToken}`;
        request.headers[`Accept-Language`] = acceptLanguage;
        request.headers[`Pragma`] = "no-cache";
        request.url = prefixApiUrl(request.url);
        logger.trace("axios.request", request);
        return request;
      },
      (error) => {
        logger.error("Request interceptor error:", error);
        return Promise.reject(error);
      }
    );

    interceptors.response.use(
      (response) => {
        let { status, headers, data, config } = response;
        const url = config.url;

        const dataType = getType(data);
        const skip = parseInt(headers["x-skip"], 10);
        const take = parseInt(headers["x-take"], 10);
        const totalFiltered = parseInt(headers["x-total-filtered"], 10);
        const totalUnfiltered = parseInt(headers["x-total-unfiltered"], 10);
        const hasPagination = skip || take || totalFiltered || totalUnfiltered;
        logger.trace("axios.response(): success", { url, dataType, hasPagination });

        if (status < 200 || status >= 300) {
          logger.debug(`axios.response(): status != OK, returning response as-is`);
          return response;
        }

        const isJsonString = dataType === "string" && (data.startsWith("{") || data.startsWith("["));
        if (isJsonString) {
          try {
            const json = JSON.parse(data);
            data = response.data = json;
          } catch (ex) {
            logger.debug(`axios.response(): Failed to parse json, error:=${ex}`);
            logger.debug(`axios.response(): data:=`, data);
          }
        }

        if (hasPagination) {
          response.data = {
            skip,
            take,
            totalFiltered,
            totalUnfiltered,
            items: data,
          };
          logger.debug(`axios.response(): fixed pagination`, response.data);
        }
        return response;
      },
      async (error: AxiosError) => {
        const errorCode = getErrorCode(error);
        const requestUrl = getRequestUrl(error);
        logger.debug("axios.response(): error", requestUrl, errorCode, error);
        throw error;
      }
    );

    return axiosInst;
  }, [acceptLanguage, acquireTokenSilent]);

  const downloadApi = useMemo(() => new DownloadApi(axiosInst, i18n), [axiosInst, i18n]);

  const boardsApi = useMemo(() => new BoardsApi(axiosInst), [axiosInst]);
  const dashboardApi = useMemo(() => new DashboardApi(axiosInst), [axiosInst]);
  const boardApi = useMemo(() => new BoardApi(axiosInst, downloadApi), [axiosInst, downloadApi]);
  const boardSendToApi = useMemo(() => new BoardSendToApi(axiosInst), [axiosInst]);
  const userApi = useMemo(() => new UserApi(axiosInst), [axiosInst]);
  const permissionsApi = useMemo(() => new PermissionsApi(axiosInst), [axiosInst]);

  const meetingsApi = useMemo(() => new MeetingsApi(axiosInst), [axiosInst]);
  const meetingApi = useMemo(() => new MeetingApi(axiosInst), [axiosInst]);
  const agendaItemsApi = useMemo(() => new AgendaItemsApi(axiosInst), [axiosInst]);
  const standardMeetingsApi = useMemo(() => new StandardMeetingsApi(axiosInst), [axiosInst]);
  const suggestedAgendaItemsApi = useMemo(() => new SuggestedAgendaItemsApi(axiosInst), [axiosInst]);
  const sendToApi = useMemo(() => new SendToApi(axiosInst), [axiosInst]);
  const participantsApi = useMemo(() => new ParticipantsApi(axiosInst), [axiosInst]);
  const tasksApi = useMemo(() => new TasksApi(axiosInst), [axiosInst]);
  const oneDriveApi = useMemo(() => new OneDriveApi(axiosInst), [axiosInst]);
  const edecideApi = useMemo(() => new EdecideApi(axiosInst), [axiosInst]);
  const workingDocumentsApi = useMemo(() => new WorkingDocumentsApi(axiosInst), [axiosInst]);
  const boardMembersApi = useMemo(() => new BoardMembersApi(axiosInst), [axiosInst]);
  const outputDocumentsApi = useMemo(() => new OutputDocumentsApi(axiosInst), [axiosInst]);
  const templatesApi = useMemo(() => new TemplatesApi(axiosInst), [axiosInst]);

  const contextValue = useMemo(
    () => ({
      boardsApi,
      boardApi,
      boardSendToApi,
      downloadApi,
      userApi,
      dashboardApi,
      meetingsApi,
      meetingApi,
      agendaItemsApi,
      standardMeetingsApi,
      suggestedAgendaItemsApi,
      sendToApi,
      participantsApi,
      tasksApi,
      oneDriveApi,
      edecideApi,
      workingDocumentsApi,
      boardMembersApi,
      outputDocumentsApi,
      permissionsApi,
      templatesApi,
    }),
    [
      boardsApi,
      boardApi,
      boardSendToApi,
      downloadApi,
      userApi,
      dashboardApi,
      meetingsApi,
      meetingApi,
      agendaItemsApi,
      standardMeetingsApi,
      suggestedAgendaItemsApi,
      sendToApi,
      participantsApi,
      tasksApi,
      oneDriveApi,
      edecideApi,
      workingDocumentsApi,
      boardMembersApi,
      outputDocumentsApi,
      permissionsApi,
      templatesApi,
    ]
  );

  return <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>;
};
